/**
 * Sign-in / sign-up form — the editorial-warm version.
 *
 * Renders inside `<AuthLayout>`'s form column. No wrapping card here
 * — the layout owns the card chrome. This component focuses on the
 * form copy, fields, password helper, and OAuth alternative.
 *
 * Mode toggle:
 *   - 'sign-up' (default for new visitors from the screener):
 *     headline reframed as "save your progress", trust copy mentions
 *     verification, password helper shows the 6-character minimum
 *   - 'sign-in': "welcome back" framing
 *
 * Error handling: same code-to-copy mapping as the previous version
 * (Phase 5 batch 3 Slice 2) — `mapAuthError` is unchanged. UX
 * around success states is upgraded: after sign-up the user sees a
 * gentle "check your inbox" inline confirmation while the Firebase
 * listener swaps the AuthGate to the verification prompt.
 */
import { type FormEvent, useState } from 'react';
import {
  type AuthClient,
  EmailNotVerifiedError,
} from '@inside-us-aos/firebase-adapter';

import { reportError, reportEvent } from '../monitoring';
import styles from './SignInForm.module.css';

export interface ISignInFormProps {
  authClient: AuthClient;
  /**
   * Initial mode. Defaults to 'sign-up' since the typical entry point
   * is from the screener "you're eligible" outcome. The form has its
   * own toggle for users who already have an account.
   */
  initialMode?: 'sign-in' | 'sign-up';
  /**
   * Called when the user clicks the in-form mode toggle. The host
   * typically navigates to the matching URL (`/sign-in` ↔
   * `/sign-up`) so the surrounding chrome (eligibility chip + journey
   * panel) updates too. When omitted, the toggle falls back to a
   * local state change — useful in tests + standalone usage.
   */
  onModeChange?: (next: 'sign-in' | 'sign-up') => void;
}

type Mode = 'sign-in' | 'sign-up';

interface IFirebaseLikeError {
  code?: string;
  message?: string;
}

function isFirebaseError(err: unknown): err is IFirebaseLikeError {
  return typeof err === 'object' && err !== null && 'code' in err;
}

function mapAuthError(err: unknown, mode: Mode): string {
  if (err instanceof EmailNotVerifiedError) {
    return (
      'Your email address is not yet verified. Check your inbox for ' +
      'the verification link, then sign in again.'
    );
  }
  const code = isFirebaseError(err) ? err.code : undefined;
  switch (code) {
    case 'auth/email-already-in-use':
      return (
        'An account already exists for this email. Try signing in instead.'
      );
    case 'auth/invalid-email':
      return 'That email address is not a valid format.';
    case 'auth/weak-password':
      return 'Your password needs to be at least 6 characters.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'That email and password do not match. Try again or reset your password.';
    case 'auth/user-not-found':
      return 'No account found for that email — create one below.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a moment, then try again.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/network-request-failed':
      return 'Network error — check your connection and try again.';
    default: {
      const message = isFirebaseError(err)
        ? err.message ?? ''
        : err instanceof Error
        ? err.message
        : '';
      return (
        message ||
        (mode === 'sign-up'
          ? 'Sign-up failed. Try again in a moment.'
          : 'Sign-in failed. Try again in a moment.')
      );
    }
  }
}

export function SignInForm({
  authClient,
  initialMode = 'sign-up',
  onModeChange,
}: ISignInFormProps): JSX.Element {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const switchMode = (next: Mode): void => {
    setError(null);
    setErrorCode(null);
    setVerifySent(false);
    if (onModeChange) {
      // Host owns the URL — let it navigate. The remounted form will
      // pick up the new initialMode from the new route.
      onModeChange(next);
      return;
    }
    setMode(next);
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [verifySent, setVerifySent] = useState(false);

  const captureError = (err: unknown, flow: string): void => {
    const code = isFirebaseError(err) ? err.code ?? null : null;
    setError(mapAuthError(err, mode));
    setErrorCode(code);
    // Most Firebase auth errors are user-actionable (bad password,
    // duplicate email, network blip) — we surface them in the UI and
    // also ship a breadcrumb event so funnel breakage is visible in
    // Sentry. Reserve `reportError` for the unmapped/unknown branch
    // so monitoring noise stays low. `code` is the only PII-safe
    // identifier we ship; the underlying Firebase error message may
    // contain the email, so we DO NOT forward `err` as-is to
    // breadcrumb attrs.
    reportEvent('auth.failed', {
      flow,
      mode,
      firebase_code: code ?? 'unknown',
    });
    if (code === null) {
      reportError(err, { kind: 'auth', flow, mode });
    }
  };

  const handleEmailSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setErrorCode(null);
    setBusy(true);
    const flow = mode === 'sign-up' ? 'sign_up_email' : 'sign_in_email';
    reportEvent('auth.attempt', { flow });
    try {
      if (mode === 'sign-up') {
        await authClient.signUpWithEmail(email, password);
        setVerifySent(true);
        reportEvent('auth.sign_up_succeeded', { provider: 'email' });
      } else {
        await authClient.signInWithEmail(email, password);
        reportEvent('auth.sign_in_succeeded', { provider: 'email' });
      }
    } catch (err) {
      captureError(err, flow);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async (): Promise<void> => {
    setError(null);
    setErrorCode(null);
    setBusy(true);
    reportEvent('auth.attempt', { flow: 'google' });
    try {
      await authClient.signInWithGoogle();
      reportEvent('auth.sign_in_succeeded', { provider: 'google' });
    } catch (err) {
      captureError(err, 'google');
    } finally {
      setBusy(false);
    }
  };

  const isSignUp = mode === 'sign-up';

  const submitLabel = busy
    ? isSignUp
      ? 'Saving…'
      : 'Signing in…'
    : isSignUp
    ? 'Save my progress'
    : 'Sign in';

  return (
    <div className={styles.form_block}>
      <header className={styles.header}>
        <h1 className={styles.headline}>
          {isSignUp ? (
            <>
              Save your progress.{' '}
              <em className={styles.headline__accent}>
                Pick up where you left off.
              </em>
            </>
          ) : (
            <>
              Welcome back to{' '}
              <em className={styles.headline__accent}>Greenbroad.</em>
            </>
          )}
        </h1>
        <p className={styles.subhead}>
          {isSignUp
            ? 'Your screener answers are stored. Create an account so you can return on any device — and so we can ship your packet when it’s ready.'
            : 'Sign in with the email you used last time, or continue with Google.'}
        </p>
      </header>

      {error ? (
        <div role="alert" className={styles.alert}>
          <span className={styles.alert__message}>{error}</span>
          {/* When the error is "email already in use", surface an
              inline navigation so the user doesn't have to hunt for
              the toggle. One-click trip from /sign-up to /sign-in. */}
          {errorCode === 'auth/email-already-in-use' ? (
            <button
              type="button"
              className={styles.alert__cta}
              onClick={() => switchMode('sign-in')}
            >
              Sign in instead →
            </button>
          ) : null}
          {errorCode === 'auth/user-not-found' && mode === 'sign-in' ? (
            <button
              type="button"
              className={styles.alert__cta}
              onClick={() => switchMode('sign-up')}
            >
              Create an account →
            </button>
          ) : null}
        </div>
      ) : null}

      {verifySent && isSignUp ? (
        <div role="status" className={styles.success_box}>
          <span className={styles.success_box__icon} aria-hidden="true">
            ✓
          </span>
          <div>
            <p className={styles.success_box__title}>Check your inbox</p>
            <p className={styles.success_box__body}>
              We sent a verification link to{' '}
              <strong>{email}</strong>. Click it and we’ll bring you back
              automatically.
            </p>
          </div>
        </div>
      ) : null}

      <form onSubmit={handleEmailSubmit} className={styles.form} noValidate>
        <label className={styles.field}>
          <span className={styles.field__label}>Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            className={styles.field__input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.field__label_row}>
            <span className={styles.field__label}>Password</span>
            {isSignUp ? (
              <span className={styles.field__hint}>at least 6 characters</span>
            ) : null}
          </span>
          <input
            type="password"
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            required
            minLength={6}
            placeholder={isSignUp ? 'Choose something memorable' : '••••••'}
            className={styles.field__input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
        </label>

        <button
          type="submit"
          className={styles.primary_cta}
          disabled={busy || email.length === 0 || password.length === 0}
        >
          {submitLabel}
          <span className={styles.primary_cta__arrow} aria-hidden="true">
            →
          </span>
        </button>
      </form>

      <div className={styles.divider}>
        <span>or one-click with</span>
      </div>

      <button
        type="button"
        className={styles.google_cta}
        onClick={() => {
          void handleGoogle();
        }}
        disabled={busy}
      >
        <GoogleGlyph />
        Continue with Google
      </button>

      <div className={styles.toggle}>
        {isSignUp ? (
          <>
            Already have an account?{' '}
            <button
              type="button"
              className={styles.toggle__link}
              onClick={() => switchMode('sign-in')}
            >
              Sign in
            </button>
          </>
        ) : (
          <>
            New here?{' '}
            <button
              type="button"
              className={styles.toggle__link}
              onClick={() => switchMode('sign-up')}
            >
              Create an account
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Inline SVG Google "G" mark. SVG is required because the rendered
 * mark must match Google's brand guidelines (4 colors, exact paths)
 * and an emoji or font won't do — also lets us color-match cleanly
 * with the surrounding button.
 */
function GoogleGlyph(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.614z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
